/**
 * WordPress dependencies
 */
import type * as wpData from '@safe-wordpress/data';
type WpData = typeof wpData;

/**
 * External dependencies
 */
import debounce from 'lodash/debounce';
import throttle from 'lodash/throttle';
import type { CssSelectorFinderState, Maybe } from '@nab/types';

/**
 * Internal dependencies
 */
import './style.scss';
import { getBreadcrumbs, isBreadcrumb, renderBreadcrumbs } from './breadcrumbs';
import { getCssPath } from './css-path';
import { getEditableContentElement } from './editable-content';
import {
	drawHoveredElement,
	highlightCssSelector,
	reactToEvents,
} from './highlighter';

// NOTE. No @nab packages in front.
import type { store as dataStore } from '@nab/data';
const NAB_DATA = 'nab/data' as unknown as typeof dataStore;

function init() {
	if ( ! hasWordPressData( window.parent ) ) {
		// eslint-disable-next-line no-console
		console.error( 'Parent window doesn’t have expected API. Bye!' );
		return;
	}

	reactToEvents();

	const state = window.parent.wp.data
		.select( NAB_DATA )
		.getPageAttribute( 'css-selector/cssSelectorFinderState' );
	if ( state ) {
		reactToParentState( undefined, state );
	}

	let running = false;
	let previousState = state;
	window.parent.wp.data.subscribe( () => {
		if ( ! hasWordPressData( window.parent ) ) {
			return;
		}

		const currentState = window.parent.wp.data
			.select( NAB_DATA )
			.getPageAttribute( 'css-selector/cssSelectorFinderState' );
		if (
			JSON.stringify( currentState ) === JSON.stringify( previousState )
		) {
			return;
		}

		const actualPreviousState = previousState;
		previousState = currentState;
		if ( ! currentState ) {
			return;
		}

		if ( ! running ) {
			running = true;
			reactToParentState( actualPreviousState, currentState );
			running = false;
		}
	} );
}

let deactivateInteractionListeners: () => void = () => void null;
function reactToParentState(
	previousState: Maybe< CssSelectorFinderState >,
	currentState: CssSelectorFinderState
): void {
	deactivateInteractionListeners();
	deactivateInteractionListeners = addInteractionListeners( currentState );

	const selector = getCssValue( currentState.mode, currentState.value );
	markElementAsSelected( selector );
	const { numberOfElements, breadcrumbs } = highlightCssSelector(
		selector,
		currentState.autoHideOnInactivity ? 'autohide' : 'permanent'
	);

	if (
		numberOfElements !== previousState?.numberOfElements ||
		JSON.stringify( breadcrumbs ) !==
			JSON.stringify( previousState.breadcrumbs )
	) {
		setParentState( {
			numberOfElements,
			breadcrumbs,
		} );
	}

	if ( currentState.mode === 'css' ) {
		renderBreadcrumbs(
			'navigation' === currentState.interactionMode ||
				'editable-content-inspector' === currentState.interactionMode
				? []
				: breadcrumbs ?? [],
			currentState.value
		);
	}
}

function addInteractionListeners(
	currentState: CssSelectorFinderState
): () => void {
	if ( 'navigation' === currentState.interactionMode ) {
		document.body.classList.remove( 'nab-hovering' );
		return () => void null;
	}

	const onMouseOver = throttle(
		drawHoveredElement(
			'editable-content-inspector' === currentState.interactionMode
		),
		100
	);
	const onClick = selectHoveredElement( currentState );

	document.body.classList.add( 'nab-hovering' );
	document.addEventListener( 'mouseover', onMouseOver );
	document.addEventListener( 'click', onClick );
	document.addEventListener( 'click', onClick, true );

	return () => {
		document.removeEventListener( 'mouseover', onMouseOver );
		document.removeEventListener( 'click', onClick );
		document.removeEventListener( 'click', onClick, true );
	};
}

function selectHoveredElement( state: CssSelectorFinderState ) {
	const processClick = mkClickProcessor( state );
	return ( ev: Event ) => {
		if ( isBreadcrumb( ev.target as HTMLElement ) ) {
			return;
		}

		ev.preventDefault();

		const el = ev.target as HTMLElement | null;
		if ( ! el ) {
			return;
		}

		processClick( el );
	};
}

const mkClickProcessor = ( state: CssSelectorFinderState ) =>
	debounce( ( el: HTMLElement ) => {
		const element =
			'editable-content-inspector' === state.interactionMode
				? getEditableContentElement( el )
				: el;
		if ( ! element ) {
			return;
		}

		if ( element.classList.contains( 'nab-highlight' ) ) {
			resetSelectedElement();
			return setParentState( {
				value: '',
				breadcrumbs: [],
			} );
		}

		const mode =
			'flexible-inspector' === state.interactionMode
				? 'multiple'
				: 'single';
		markElementAsSelected( element );
		const selector = getCssPath( element, mode ) || '';

		setParentState( {
			value: selector,
			breadcrumbs: getBreadcrumbs( state.breadcrumbs, selector ),
		} );
	}, 1 );

export function setParentState(
	newState: Partial< CssSelectorFinderState >
): void {
	if ( ! hasWordPressData( window.parent ) ) {
		return;
	}

	const { select, dispatch } = window.parent.wp.data;
	const state = select( NAB_DATA ).getPageAttribute(
		'css-selector/cssSelectorFinderState'
	);

	if ( undefined === state ) {
		return;
	}

	void dispatch( NAB_DATA ).setPageAttribute(
		'css-selector/cssSelectorFinderState',
		{ ...state, ...newState }
	);
}

const CUSTOM_CLASS = 'nab-selected';
function markElementAsSelected( element: string | HTMLElement | null ): void {
	try {
		if ( 'string' === typeof element ) {
			const selector = element;
			const elements = [ ...document.querySelectorAll( selector ) ];
			if (
				elements.some( ( e ) => e.classList.contains( CUSTOM_CLASS ) )
			) {
				return;
			}
			element = ( elements[ 0 ] as HTMLElement ) ?? null;
		}

		resetSelectedElement();
		element?.classList.add( CUSTOM_CLASS );
	} catch ( _ ) {}
}

function getSelectedElement(): HTMLElement | null {
	return document.querySelector( `.${ CUSTOM_CLASS }` );
}

function resetSelectedElement() {
	getSelectedElement()?.classList.remove( CUSTOM_CLASS );
}

function getCssValue( mode: 'id' | 'class' | 'css', value = '' ): string {
	if ( ! value.trim() ) {
		return '';
	}
	switch ( mode ) {
		case 'id':
			return `#${ value.replace( /\s/g, '' ) }`;
		case 'class':
			return `.${ value.replace( /\s/g, '' ) }`;
		case 'css':
			return value;
	}
}

const hasWordPressData = (
	win: unknown
): win is {
	readonly wp: {
		readonly data: WpData;
	};
} =>
	// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
	!! ( win as any )?.wp?.data;

init();
